import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Save, CheckCircle, XCircle, DollarSign, ChevronDown, ChevronUp } from "lucide-react";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = "http://127.0.0.1:8000";
const API = `${BACKEND_URL}/api/v1`;

export const AdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [metals, setMetals] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editingMetal, setEditingMetal] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [metalsRes, ordersRes, customersRes] = await Promise.all([
        axios.get(`${API}/admin/metals`),
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/customers`),
      ]);
      setMetals(metalsRes.data);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading admin data:", error);
      toast.error("Failed to load admin data");
      setLoading(false);
    }
  };

  const handleUpdateMetal = async (metalId) => {
    if (!editingMetal) return;

    try {
      await axios.put(`${API}/admin/metals/${metalId}`, editingMetal);
      toast.success("Metal updated successfully");
      setEditingMetal(null);
      loadData();
    } catch (error) {
      console.error("Error updating metal:", error);
      toast.error("Failed to update metal");
    }
  };

  const [expandedOrder, setExpandedOrder] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null);

  const handleApproveCustomer = async (userId) => {
    try {
      await axios.put(`${API}/admin/customers/${userId}/approve`);
      toast.success("Customer approved — approval email sent");
      loadData();
    } catch { toast.error("Failed to approve customer"); }
  };

  const handleRejectCustomer = async (userId) => {
    try {
      await axios.put(`${API}/admin/customers/${userId}/reject`);
      toast.success("Customer rejected");
      loadData();
    } catch { toast.error("Failed to reject customer"); }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${newStatus}`);
      toast.success("Order status updated");
      loadData();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const handleApprove = async (orderId) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/approve`);
      toast.success("Order approved");
      loadData();
    } catch (error) {
      toast.error("Failed to approve order");
    }
  };

  const handleDecline = async (orderId) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/decline`);
      toast.success("Order declined");
      loadData();
    } catch (error) {
      toast.error("Failed to decline order");
    }
  };

  const handleMarkPaid = async (orderId) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/mark-paid`);
      toast.success("Payment marked as received");
      loadData();
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const statusOptions = [
    "pending",
    "received",
    "printing",
    "investing",
    "casting",
    "finishing",
    "shipped",
    "delivered",
  ];

  return (
    <div className="min-h-screen bg-background py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <h1
          className="font-heading text-4xl font-bold mb-8"
          data-testid="admin-title"
        >
          Admin Dashboard
        </h1>

        <Tabs defaultValue="customers" className="w-full">
          <TabsList className="mb-8">
            <TabsTrigger value="customers">Customers</TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab">Orders</TabsTrigger>
            <TabsTrigger value="metals" data-testid="metals-tab">Metal Pricing</TabsTrigger>
          </TabsList>

          {/* ── CUSTOMERS TAB ── */}
          <TabsContent value="customers">
            <div className="space-y-3">
              {customers.length === 0 && (
                <div className="bg-white border border-border rounded-md text-center py-12 text-muted-foreground">
                  No customer registrations yet
                </div>
              )}
              {customers.map((c) => {
                const isOpen = expandedCustomer === c.id;
                const statusCls =
                  c.account_status === "approved" ? "bg-green-100 text-green-800"
                  : c.account_status === "rejected" ? "bg-red-100 text-red-800"
                  : "bg-amber-100 text-amber-800";
                return (
                  <div key={c.id} className="bg-white border border-border rounded-md overflow-hidden">
                    <div
                      className="flex flex-wrap items-center gap-4 px-6 py-4 cursor-pointer hover:bg-secondary/40"
                      onClick={() => setExpandedCustomer(isOpen ? null : c.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{c.first_name} {c.last_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{c.company_name} · {c.email}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusCls}`}>
                        {c.account_status}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.is_verified ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {c.is_verified ? "Email verified" : "Email unverified"}
                      </span>
                      <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</span>
                      {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>

                    {isOpen && (
                      <div className="border-t border-border px-6 py-5 space-y-4">
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div className="space-y-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Business</p>
                            <p>{c.company_name}</p>
                            <p className="text-muted-foreground">{c.company_email}</p>
                            <p className="text-muted-foreground">{c.business_address}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contact</p>
                            <p>{c.first_name} {c.last_name}</p>
                            <p className="text-muted-foreground">{c.phone}</p>
                            <p className="text-muted-foreground font-mono">EIN: {c.ein_number}</p>
                          </div>
                        </div>

                        {(c.id_document_path || c.ein_certificate_path) && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Documents</p>
                            <div className="flex gap-3 flex-wrap">
                              {c.id_document_path && (
                                <a href={`http://127.0.0.1:8000/uploads/${c.id_document_path}`} target="_blank" rel="noreferrer"
                                  className="text-xs text-accent underline">View Government ID</a>
                              )}
                              {c.ein_certificate_path && (
                                <a href={`http://127.0.0.1:8000/uploads/${c.ein_certificate_path}`} target="_blank" rel="noreferrer"
                                  className="text-xs text-accent underline">View EIN Certificate</a>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 pt-2 border-t border-border flex-wrap">
                          {c.account_status !== "approved" && (
                            <Button size="sm" onClick={() => handleApproveCustomer(c.id)}
                              className="bg-green-600 hover:bg-green-700 text-white gap-1">
                              <CheckCircle className="w-4 h-4" /> Approve
                            </Button>
                          )}
                          {c.account_status !== "rejected" && (
                            <Button size="sm" variant="outline" onClick={() => handleRejectCustomer(c.id)}
                              className="border-red-300 text-red-600 hover:bg-red-50 gap-1">
                              <XCircle className="w-4 h-4" /> Reject
                            </Button>
                          )}
                          {c.account_status === "approved" && (
                            <span className="text-xs text-green-700 font-medium self-center">Account approved ✓</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* ── ORDERS TAB ── */}
          <TabsContent value="orders">
            <div className="space-y-4">
              {orders.length === 0 && (
                <div className="bg-white border border-border rounded-md text-center py-12 text-muted-foreground" data-testid="no-orders">
                  No orders yet
                </div>
              )}
              {orders.map((order, idx) => {
                const isExpanded = expandedOrder === order.id;
                const approvalCls =
                  order.approval_status === "approved" ? "bg-green-100 text-green-800"
                  : order.approval_status === "declined" ? "bg-red-100 text-red-800"
                  : "bg-amber-100 text-amber-800";
                const paymentCls = order.payment_status === "paid"
                  ? "bg-green-100 text-green-800"
                  : "bg-amber-100 text-amber-800";

                return (
                  <div key={order.id} className="bg-white border border-border rounded-md overflow-hidden" data-testid={`order-row-${idx}`}>

                    {/* Summary row */}
                    <div
                      className="flex flex-wrap items-center gap-4 px-6 py-4 cursor-pointer hover:bg-secondary/40"
                      onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                    >
                      <span className="font-mono text-sm font-semibold w-24 shrink-0">#{order.id.slice(0, 8)}</span>
                      <span className="text-sm text-muted-foreground flex-1 min-w-0 truncate">{order.contact_email}</span>
                      <span className="font-mono text-sm font-semibold">${order.total.toFixed(2)}</span>
                      <span className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${approvalCls}`}>
                        {order.approval_status ?? "pending"}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${paymentCls}`}>
                        {order.payment_status === "paid" ? "Paid" : "Unpaid"}
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="border-t border-border px-6 py-5 space-y-5">

                        {/* Items */}
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Order Items</p>
                          <div className="space-y-1">
                            {order.items.map((item, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span>{item.filename} — {item.metal_name} × {item.quantity}</span>
                                <span className="font-mono">${item.line_total.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Addresses + notes */}
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Shipping Address</p>
                            <p>{order.shipping_address.address_line1}</p>
                            <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
                          </div>
                          {order.order_notes && (
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Notes</p>
                              <p className="text-muted-foreground">{order.order_notes}</p>
                            </div>
                          )}
                        </div>

                        {/* Production status */}
                        <div className="flex items-center gap-3">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider shrink-0">Production</p>
                          <select
                            value={order.status}
                            onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                            className="border border-input rounded-sm px-2 py-1 text-xs"
                            data-testid={`order-status-${idx}`}
                          >
                            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>

                        {/* Action buttons */}
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                          {order.approval_status !== "approved" && (
                            <Button size="sm" onClick={() => handleApprove(order.id)}
                              className="bg-green-600 hover:bg-green-700 text-white gap-1">
                              <CheckCircle className="w-4 h-4" /> Approve
                            </Button>
                          )}
                          {order.approval_status !== "declined" && (
                            <Button size="sm" variant="outline" onClick={() => handleDecline(order.id)}
                              className="border-red-300 text-red-600 hover:bg-red-50 gap-1">
                              <XCircle className="w-4 h-4" /> Decline
                            </Button>
                          )}
                          {order.payment_status !== "paid" && (
                            <Button size="sm" onClick={() => handleMarkPaid(order.id)}
                              className="bg-accent hover:bg-accent-hover text-white gap-1">
                              <DollarSign className="w-4 h-4" /> Mark Payment Received
                            </Button>
                          )}
                          {order.payment_status === "paid" && order.approval_status === "approved" && (
                            <span className="text-xs text-green-700 font-medium self-center">All done ✓</span>
                          )}
                        </div>

                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="metals">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {metals.map((metal, idx) => (
                <div
                  key={metal.id}
                  className="bg-white border border-border p-6 rounded-md"
                  data-testid={`metal-card-${idx}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-12 h-12 rounded-sm"
                      style={{ backgroundColor: metal.color_hex }}
                    />
                    <h3 className="font-semibold text-lg">{metal.name}</h3>
                  </div>

                  {editingMetal?.id === metal.id ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-muted-foreground">
                          Density (g/cm³)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={editingMetal.density}
                          onChange={(e) =>
                            setEditingMetal({
                              ...editingMetal,
                              density: parseFloat(e.target.value),
                            })
                          }
                          className="w-full border border-input rounded-sm px-3 py-2 mt-1"
                          data-testid={`metal-density-${idx}`}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">
                          Rate ($/gram)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={editingMetal.rate_per_gram}
                          onChange={(e) =>
                            setEditingMetal({
                              ...editingMetal,
                              rate_per_gram: parseFloat(e.target.value),
                            })
                          }
                          className="w-full border border-input rounded-sm px-3 py-2 mt-1"
                          data-testid={`metal-rate-${idx}`}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleUpdateMetal(metal.id)}
                          className="flex-1 bg-accent hover:bg-accent-hover text-white"
                          size="sm"
                          data-testid={`save-metal-${idx}`}
                        >
                          <Save className="w-4 h-4 mr-1" />
                          Save
                        </Button>
                        <Button
                          onClick={() => setEditingMetal(null)}
                          variant="outline"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Density:
                          </span>
                          <span className="font-mono">
                            {metal.density} g/cm³
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Rate:</span>
                          <span className="font-mono">
                            ${metal.rate_per_gram}/g
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={() => setEditingMetal(metal)}
                        variant="outline"
                        size="sm"
                        className="w-full"
                        data-testid={`edit-metal-${idx}`}
                      >
                        Edit Pricing
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
